'use client';

import { AppSidebar } from '@/components/app-sidebar';
import { SiteHeader } from '@/components/site-header';
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar';
import { DateRangeProvider } from '@/contexts/date-range-context';
import { useSession } from '@/hooks/use-session';
import { Loader2 } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';

export default function ProtectedLayout({ children }: { children: React.ReactNode }) {
  const { session, isLoading, error, deleteSession } = useSession();
  const router = useRouter();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted || isLoading) return;

    if (error || !session) {
      router.push('/sign-in');
    }
  }, [error, session, router, isLoading, mounted]);

  if (!mounted || isLoading) {
    return (
      <div className="flex min-h-svh flex-col items-center justify-center bg-[#0A0D12]">
        <Loader2 className="animate-spin text-white/40" />
        <p className="mt-3 text-sm text-white/40">Loading...</p>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="flex min-h-svh flex-col items-center justify-center bg-[#0A0D12]">
        <Loader2 className="animate-spin text-white/40" />
        <p className="mt-3 text-sm text-white/40">Redirecting to sign in...</p>
      </div>
    );
  }

  const isAdmin = session.role === 'admin';
  const isSuperAdmin = session.role === 'super-admin';
  const isMemecoins = session.role === 'memecoins';

  if (!isAdmin && !isMemecoins && !isSuperAdmin) {
    deleteSession();

    return (
      <div className="flex min-h-svh flex-col items-center justify-center bg-[#0A0D12]">
        <h1 className="font-bold text-2xl text-red-400">Access Denied</h1>
        <p className="mt-2 text-sm text-white/40">You need admin privileges to access this area.</p>
      </div>
    );
  }

  return (
    <SidebarProvider
      style={
        {
          '--sidebar-width': '15rem',
          '--header-height': 'calc(var(--spacing) * 12)'
        } as React.CSSProperties
      }
    >
      <AppSidebar variant="inset" />
      <SidebarInset>
        <DateRangeProvider>
          <SiteHeader />
          <div className="flex flex-1 flex-col">{children}</div>
        </DateRangeProvider>
      </SidebarInset>
    </SidebarProvider>
  );
}
